import { createApi } from "@reduxjs/toolkit/query/react";
import { cacheDurationSeconds } from "common/constants";
import { ISupportForm } from "redux/utils/interfaces";
import { baseQueryWithReauth } from "./utils/constants";
import { ActionType } from "redux/utils/types";

import {
	UserParams,
	createUserParams,
	createPlanParams,
	savePlanParams
} from "./utils/interfaces";


const getUrlParams = (args) => {
	if (!args.sortBy && !args.filterBy) {
		return `limit=${args.limit}&offset=${args.offset}`;
	} else if (args.sortBy && !args.filterBy) {
		return `limit=${args.limit}&sortBy=${args.sortBy}&offset=${args.offset}`;
	} else if (!args.sortBy && args.filterBy) {
		return `limit=${args.limit}&offset=${args.offset}&filterBy=${args.filterBy}`;
	} else if (args.sortBy && args.filterBy) {
		return `limit=${args.limit}&sortBy=${args.sortBy}&offset=${args.offset}&filterBy=${args.filterBy}`;
	}
	return `limit=${args.limit}&offset=${args.offset}`;
};

const getUrlParamspec = (args) => {
	let offset = 0
	if (!args.sortBy && !args.filterBy) {
		return `limit=99&offset=${offset}`;
	} else if (args.sortBy && !args.filterBy) {
		return `limit=99&sortBy=${args.sortBy}&offset=${offset}`;
	} else if (!args.sortBy && args.filterBy) {
		return `limit=99&offset=${offset}&filterBy=${args.filterBy}`;
	} else if (args.sortBy && args.filterBy) {
		return `limit=99&sortBy=${args.sortBy}&offset=${offset}&filterBy=${args.filterBy}`;
	}
	return `limit=99&offset=${offset}`;
};

const getUrlParamsForTransactions = (args) => {
	if (!args.sortBy) {
		return `userName=${args.userName}&locationId.stationId=${args.locationIdStationId}&limit=${args.limit}&offset=${args.offset}&chargerId=${args.chargerId}`;
	}
	return `userName=${args.userName}&locationId.stationId=${args.locationIdStationId}&limit=${args.limit}&sortBy=${args.sortBy}&offset=${args.offset}&chargerId=${args.chargerId}`;
};

export const pricingApi = createApi({
	reducerPath: "pricing",
	baseQuery: baseQueryWithReauth,
	keepUnusedDataFor: cacheDurationSeconds,
	endpoints: (builder) => ({
		getPricingPlans: builder.query<any, any>({
			query: (args) => ({
				url: `get-pricing-plans?${getUrlParamspec(args)}`,
				keepUnusedDataFor: 0
			}),
		}),
		getPricingPlanDetails: builder.query<any, any>({
			query: (args) => {
				const { pricingPlanName } = args
				return {
					url: `get-pricing-plan-details?pricingPlanName=${pricingPlanName}`,
				}
			},
		}),
		getStationPricingPlans: builder.query<any, UserParams>({
			query: (args) => ({
				url: `get-station-pricing-plans-v2?${getUrlParamspec(args)}`,
				// keepUnusedDataFor: 0
			}),
		}),
		createPricingPlanV2: builder.mutation<any, any>({
			query: (data) => {
				const { accessType, pricingPlanName, pricingPlanType, priorityType, serviceFee, pricingSchedules, createdAt,
					defaultIdleRate, defaultRate, unit, authorizedAmount, maxChargeCutoffTime, managementFee,
					parkingFee, minimumCharge, maxChargePerDay } = data;
				return {
					url: "create-pricing-plan-v2",
					method: "PUT",
					body: {
						"accessType": accessType,
						"pricingPlanName": pricingPlanName,
						"pricingPlanType": pricingPlanType,
						"priorityType": priorityType,
						"serviceFee": serviceFee ? serviceFee : 0,
						"pricingSchedules": pricingSchedules ? pricingSchedules : [],
						"createdAt": createdAt ? createdAt : "",
						"defaultIdleRate": defaultIdleRate ? defaultIdleRate : 0,
						"defaultRate": defaultRate ? defaultRate : 0,
						"unit": unit ? unit : "",
						"authorizedAmount": authorizedAmount ? authorizedAmount : 0,
						"maxChargeCutoffTime": maxChargeCutoffTime ? maxChargeCutoffTime : "",
						"managementFee": managementFee ? managementFee : 0,
						"parkingFee": parkingFee ? parkingFee : {},
						"minimumCharge": minimumCharge ? minimumCharge : 0,
						"maxChargePerDay": maxChargePerDay ? maxChargePerDay : 0,
					}
				}
			},
		}),
		savePricingPlanV2: builder.mutation<any, any>({
			query: (data) => {
				const { accessType, pricingPlanOldName, pricingPlanNewName, pricingPlanType, priorityType, serviceFee, pricingSchedules, createdAt,
					defaultIdleRate, defaultRate, unit, authorizedAmount, maxChargeCutoffTime, managementFee,
					parkingFee, minimumCharge, maxChargePerDay } = data;
				return {
					url: "save-pricing-plan-v2",
					method: "PUT",
					body: {
						"accessType": accessType,
						"pricingPlanOldName": pricingPlanOldName,
						"pricingPlanNewName": pricingPlanNewName ? pricingPlanNewName : "",
						"pricingPlanType": pricingPlanType,
						"priorityType": priorityType,
						"serviceFee": serviceFee ? serviceFee : 0,
						"pricingSchedules": pricingSchedules ? pricingSchedules : [],
						"createdAt": createdAt ? createdAt : "",
						"defaultIdleRate": defaultIdleRate ? defaultIdleRate : 0,
						"defaultRate": defaultRate ? defaultRate : 0,
						"unit": unit ? unit : "",
						"authorizedAmount": authorizedAmount ? authorizedAmount : 0,
						"maxChargeCutoffTime": maxChargeCutoffTime ? maxChargeCutoffTime : "",
						"managementFee": managementFee ? managementFee : 0,
						"parkingFee": parkingFee ? parkingFee : {},
						"minimumCharge": minimumCharge ? minimumCharge : 0,
						"maxChargePerDay": maxChargePerDay ? maxChargePerDay : 0,
					}
				}
			},
		}),
		EditPricingPlanV2: builder.mutation<any, any>({
			query: (data) => {
				const { station, unifiedPricingPlanName } = data;
				return {
					url: "save-station-pricing-plan-v2",
					method: "PUT",
					body: {
						station,
						unifiedPricingPlanName: unifiedPricingPlanName,
					}
				}
			},
		}),
		DeletePricingPlanV2: builder.mutation<any, any>({
			query: (data) => {
				const { pricingPlanToDelete } = data;
				return {
					url: "delete-pricing-plans",
					method: "PUT",
					body: {
						pricingPlanToDelete: pricingPlanToDelete
					}
				}
			},
		}),
		getPlanDetails: builder.query<any, { userName: string, planName: string }>({
			query: (args) => {
				if (!args.planName) {
					return {
						url: ''
					}
				}
				return {
					url: `get-pricing-plan-details?userName=${args.userName}&pricingPlanName=${args.planName}`,
					keepUnusedDataFor: 0
				}
			},
		}),
		createPlan: builder.mutation<ActionType, createPlanParams>({
			query: (data) => {
				let body: any = null
				const { pricingPlanName, pricingPlanType, minimumCharge, defaultRate, unit, pricingSchedules } = data
				if (pricingPlanType === 'plug_in_time_based') {
					const { maxChargePerDay, maxChargeCutoff } = data
					body = {
						"pricingPlanName": pricingPlanName,
						// "pricingPlanType": pricingPlanType,
						"minimumCharge": minimumCharge,
						"maxChargePerDay": maxChargePerDay,
						"maxChargeCutoff": maxChargeCutoff,
						"defaultRate": defaultRate,
						"unit": unit,
						"pricingSchedules": pricingSchedules
					}
				}
				if (pricingPlanType === 'energy_based') {
					const { parkingRate, idleRate } = data
					body = {
						"pricingPlanName": pricingPlanName,
						"pricingPlanType": pricingPlanType,
						"minimumCharge": minimumCharge,
						"parkingRate": parkingRate,
						"idleRate": idleRate,
						"defaultRate": defaultRate,
						"unit": unit,
						"pricingSchedules": pricingSchedules
					}
				}
				if (pricingPlanType === 'charging_time_based') {
					const { maxChargePerDay, maxChargeCutoff, parkingRate, idleRate } = data
					body = {
						"pricingPlanName": pricingPlanName,
						"pricingPlanType": pricingPlanType,
						"minimumCharge": minimumCharge,
						"parkingRate": parkingRate,
						"maxChargePerDay": maxChargePerDay,
						"maxChargeCutoff": maxChargeCutoff,
						"idleRate": idleRate,
						"defaultRate": defaultRate,
						"unit": unit,
						"pricingSchedules": pricingSchedules
					}
				}
				if (pricingPlanType === 'DCFC') {
					const { parkingRate, idleRate } = data
					body = {
						"pricingPlanName": pricingPlanName,
						"pricingPlanType": pricingPlanType,
						"minimumCharge": minimumCharge,
						"parkingRate": parkingRate,
						"idleRate": idleRate,
						"defaultRate": defaultRate,
						"unit": unit,
						"pricingSchedules": pricingSchedules
					}
				}
				return {
					url: "create-pricing-plan-v2",
					method: "PUT",
					body: body
				};
			}
		}),
		savePlan: builder.mutation<ActionType, savePlanParams>({
			query: (data) => {
				let body: any = null
				const { pricingPlanOldName, pricingPlanNewName, pricingPlanType, minimumCharge, defaultRate, unit, pricingSchedules } = data
				if (pricingPlanType === 'plug_in_time_based') {
					const { maxChargePerDay, maxChargeCutoff } = data
					body = {
						"pricingPlanOldName": pricingPlanOldName,
						"pricingPlanNewName": pricingPlanNewName,
						// "pricingPlanType": pricingPlanType,
						"minimumCharge": minimumCharge,
						"maxChargePerDay": maxChargePerDay,
						"maxChargeCutoff": maxChargeCutoff,
						"defaultRate": defaultRate,
						"unit": unit,
						"pricingSchedules": pricingSchedules
					}
				}
				if (pricingPlanType === 'energy_based') {
					const { parkingRate, idleRate } = data
					body = {
						"pricingPlanOldName": pricingPlanOldName,
						"pricingPlanNewName": pricingPlanNewName,
						"pricingPlanType": pricingPlanType,
						"minimumCharge": minimumCharge,
						"parkingRate": parkingRate,
						"idleRate": idleRate,
						"defaultRate": defaultRate,
						"unit": unit,
						"pricingSchedules": pricingSchedules
					}
				}
				if (pricingPlanType === 'charging_time_based') {
					const { maxChargePerDay, maxChargeCutoff, parkingRate, idleRate } = data
					body = {
						"pricingPlanOldName": pricingPlanOldName,
						"pricingPlanNewName": pricingPlanNewName,
						"pricingPlanType": pricingPlanType,
						"minimumCharge": minimumCharge,
						"parkingRate": parkingRate,
						"maxChargePerDay": maxChargePerDay,
						"maxChargeCutoff": maxChargeCutoff,
						"idleRate": idleRate,
						"defaultRate": defaultRate,
						"unit": unit,
						"pricingSchedules": pricingSchedules
					}
				}
				if (pricingPlanType === 'DCFC') {
					const { parkingRate, idleRate } = data
					body = {
						"pricingPlanOldName": pricingPlanOldName,
						"pricingPlanNewName": pricingPlanNewName,
						"pricingPlanType": pricingPlanType,
						"minimumCharge": minimumCharge,
						"parkingRate": parkingRate,
						"idleRate": idleRate,
						"defaultRate": defaultRate,
						"unit": unit,
						"pricingSchedules": pricingSchedules
					}
				}
				return {
					url: "save-pricing-plan-v2",
					method: "PUT",
					body: body
				};
			}
		}),
		getStationPricingPlanDetails: builder.query<any, any>({
			query: (data) => {
				const { station, pricingPlanName } = data
				return {
					url: `get-station-pricing-plan-details?locationId.stationId=${station}&pricingPlanName=${pricingPlanName}`,
					keepUnusedDataFor: 0
				}
			},
		})
	}),
});

export const {
	useGetPricingPlansQuery,
	useGetPricingPlanDetailsQuery,
	useGetStationPricingPlansQuery,
	useCreatePricingPlanV2Mutation,
	useSavePricingPlanV2Mutation,
	useEditPricingPlanV2Mutation,
	useDeletePricingPlanV2Mutation,
	useGetPlanDetailsQuery,
	useSavePlanMutation,
	useCreatePlanMutation,
	useGetStationPricingPlanDetailsQuery
} =
	pricingApi;

